import { DPR, NODE_DEPTH } from '@constants/common.const';
import { NodeVisualState } from '@enums/node-states.enum';
import type { NodeLayoutItem } from '@models/layout';
import type { NodeConfig, NodeView } from '@models/nodes';
import Phaser from 'phaser';
import { getStrategy, StateChangeHelpers } from './strategies';
import { GameConfigService } from '@services/core/GameConfigService';

export class NodeViewFactory {
  private outlineRT: Phaser.GameObjects.RenderTexture | undefined;
  private configService = new GameConfigService();

  constructor(private readonly rewardLayer: Phaser.GameObjects.Container) {}

  public create(
    scene: Phaser.Scene,
    node: NodeLayoutItem,
    config: NodeConfig,
    nodesLayer: Phaser.GameObjects.Container,
  ): NodeView {
    const strategy = getStrategy(node.type);
    const { sprite, scaleY, scaleX } = strategy.getDefaultNodeImage(
      scene,
      node.action,
    );

    // Position sprite directly — no Container wrapper
    sprite.setPosition(node.x, node.y);
    nodesLayer.add(sprite);

    // Create extras (e.g., reward icon) via strategy
    const extras = strategy.createExtras(scene, node, config);
    const rewardIcon = extras?.rewardIcon;

    const cullState = { culled: false };
    const { stopOutline, startOutline, setOutlineVisible } =
      this.createOutlineHelpers(scene, nodesLayer, node, config, cullState);
    const { stopPulse, startPulse } = this.createPulseHelpers(
      scene,
      sprite,
      scaleX,
      scaleY,
    );

    const helpers: StateChangeHelpers = {
      stopOutline,
      startOutline,
      stopPulse,
      startPulse,
    };

    const view: NodeView = {
      index: node.index,
      type: node.type,
      action: node.action,
      sprite,
      rewardIcon,
      state: NodeVisualState.Default,
      setState: (next) => {
        if (view.state === next) return;
        view.state = next;

        // we don't need to change a texture key for all strategy
        // some strategies keep the same texture for all states (e.g. checkpoint)
        const textureKey = strategy.getTextureForState(next, scene);
        if (textureKey) {
          sprite.setTexture(textureKey);
        }

        // Delegate state change handling to strategy
        strategy.onStateChange(view, next, helpers);
      },
      startOutline,
      // Off-screen views are hidden (skips their render + FX passes entirely)
      // and their tweens paused, so only nodes near the viewport cost anything.
      setCulled: (culled) => {
        if (cullState.culled === culled) return;
        cullState.culled = culled;

        const visible = !culled;
        sprite.setVisible(visible);
        rewardIcon?.setVisible(visible);
        setOutlineVisible(visible);

        const targets = rewardIcon ? [sprite, rewardIcon] : [sprite];
        for (const target of targets) {
          for (const tween of scene.tweens.getTweensOf(target)) {
            if (culled) tween.pause();
            else tween.resume();
          }
        }
      },
      destroy: () => {
        stopOutline();
        stopPulse();
        if (rewardIcon) {
          scene.tweens.killTweensOf(rewardIcon);
          rewardIcon.destroy();
        }
        sprite.destroy();
      },
    };

    if (rewardIcon) {
      this.rewardLayer.add(rewardIcon);
      rewardIcon.setScrollFactor(sprite.scrollFactorX, sprite.scrollFactorY);
      view.rewardIcon = rewardIcon;
    }

    view.setState(NodeVisualState.Default);
    return view;
  }

  /** Release the shared outline texture. Call from NodeService.destroy(). */
  public dispose(): void {
    this.outlineRT?.destroy();
    this.outlineRT = undefined;
  }

  private createOutlineHelpers(
    scene: Phaser.Scene,
    nodesLayer: Phaser.GameObjects.Container,
    node: NodeLayoutItem,
    config: NodeConfig,
    cullState: { culled: boolean },
  ) {
    let outlineImg: Phaser.GameObjects.Image | undefined;

    const stopOutline = () => {
      outlineImg?.destroy();
      outlineImg = undefined;
    };

    const startOutline = () => {
      stopOutline();
      const tex = this.getOrBakeOutlineTexture(scene, config);
      outlineImg = scene.add.image(node.x, node.y, tex);
      outlineImg.setDepth(NODE_DEPTH - 1);
      outlineImg.setVisible(!cullState.culled);
      nodesLayer.add(outlineImg);
      nodesLayer.sort('depth');
    };

    const setOutlineVisible = (visible: boolean) => {
      outlineImg?.setVisible(visible);
    };

    return { stopOutline, startOutline, setOutlineVisible };
  }

  /** Pre-bake the 3-ring outline into a RenderTexture once; reuse for every active node. */
  private getOrBakeOutlineTexture(
    scene: Phaser.Scene,
    config: NodeConfig,
  ): Phaser.Textures.Texture {
    if (this.outlineRT) return this.outlineRT.texture;

    const lineWidth = 2 * DPR;
    const w = config.width + 10;
    const h = config.height + 10;
    const gap = 12;

    const outerW = w + (gap + lineWidth) * 2;
    const outerH = h + (gap + lineWidth) * 2;
    const pad = lineWidth * 2;
    const rtW = Math.ceil(outerW + pad * 2);
    const rtH = Math.ceil(outerH + pad * 2);
    const cx = rtW / 2;
    const cy = rtH / 2;

    this.outlineRT = scene.add.renderTexture(0, 0, rtW, rtH);
    this.outlineRT.setVisible(false);

    const gfx = scene.add.graphics();
    const outlineColor =
      this.configService.getColor('--z-pad-outline-color') ??
      this.configService.getColor('--color-accent-2')!;
    gfx.lineStyle(lineWidth, outlineColor, 0.4).strokeEllipse(cx, cy, w, h);
    gfx
      .lineStyle(lineWidth, outlineColor, 0.2)
      .strokeEllipse(cx, cy, w + gap + lineWidth, h + gap + lineWidth);
    gfx
      .lineStyle(lineWidth, outlineColor, 0.1)
      .strokeEllipse(cx, cy, outerW, outerH);
    this.outlineRT.draw(gfx, 0, 0);
    gfx.destroy();

    return this.outlineRT.texture;
  }

  private createPulseHelpers(
    scene: Phaser.Scene,
    sprite: Phaser.GameObjects.Image,
    baseScaleX: number,
    baseScaleY: number,
  ) {
    let tween: Phaser.Tweens.Tween | undefined;

    const stopPulse = () => {
      tween?.stop();
      tween = undefined;
      scene.tweens.killTweensOf(sprite);
      sprite.setScale(baseScaleX, baseScaleY);
    };

    const startPulse = () => {
      stopPulse();
      tween = scene.tweens.add({
        targets: sprite,
        scaleX: baseScaleX * 1.04,
        scaleY: baseScaleY * 1.04,
        duration: 420,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    };

    return { stopPulse, startPulse };
  }
}
